import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, map, Observable, of } from 'rxjs';
import { AccountRoleInfo } from '../models/account-role-info';
import { CurrentUser } from '../models/current-user';
import { userAuthRoleFromClaim } from '../models/user-role';

@Injectable({
    providedIn: 'root',
})
export class CurrentUserService {
    private cachedAccounts: AccountRoleInfo[] = [];
    private msalReady = false;

    constructor(
        private readonly authService: MsalService,
        private readonly destroyRef: DestroyRef,
        private readonly msalBroadcastService: MsalBroadcastService,
        private readonly router: Router
    ) {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status) => status === InteractionStatus.None),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.msalReady = true;
                const accounts = this.authService.instance.getAllAccounts();
                this.cachedAccounts = accounts as AccountRoleInfo[];

                if (
                    !this.authService.instance.getActiveAccount() &&
                    accounts.length > 0
                ) {
                    this.authService.instance.setActiveAccount(accounts[0]);
                }
            });
    }

    getCurrentUser(): CurrentUser | null {
        if (!this.msalReady) {
            return null;
        }
        const accounts = this.cachedAccounts;

        if (accounts.length === 0) {
            return null;
        } else if (accounts.length === 1) {
            const account: AccountRoleInfo = accounts[0];
            return new CurrentUser(
                account.localAccountId,
                this.getAccountEmail(account),
                account.name,
                userAuthRoleFromClaim(account.idTokenClaims?.roles?.[0])
            );
        } else {
            this.router.navigate(['/logout']);
            return null;
        }
    }

    /**
     * Attempts to silently refresh the current account's cached ID token and re-derive
     * the current user from the result.
     *
     * Intended as a last resort for guards: an account can be present with a stale or
     * missing role claim (e.g. its cached ID token was evicted - such as by a dev-server
     * hot-reload race, or by normal expiry) even though the account's SSO session with
     * the identity provider is still valid. `acquireTokenSilent` renews the session via
     * a hidden iframe using that session, without prompting the user, and MSAL persists
     * the refreshed ID token/claims as a side effect - so a subsequent `getCurrentUser()`
     * reflects the up-to-date role.
     *
     * Resolves to whatever `getCurrentUser()` returns even on failure (e.g. the SSO
     * session itself has actually expired) - callers should treat a still-missing/
     * mismatched role after this as a genuine authorization failure.
     */
    refreshCurrentUser(): Observable<CurrentUser | null> {
        const account = this.authService.instance.getActiveAccount();

        if (!account) {
            return of(this.getCurrentUser());
        }

        return this.authService
            .acquireTokenSilent({ account, scopes: ['openid', 'profile'] })
            .pipe(
                map(() => {
                    this.cachedAccounts =
                        this.authService.instance.getAllAccounts() as AccountRoleInfo[];
                    return this.getCurrentUser();
                }),
                catchError(() => of(this.getCurrentUser()))
            );
    }

    private getAccountEmail(account: AccountRoleInfo): string {
        return account.idTokenClaims?.emails?.[0] ?? account.username ?? '';
    }
}
